import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { authAPI } from '../services/api';
import type { ActivityTrend } from '../api/BackofficeApi';
import { useAuth } from '../contexts/AuthContext';

interface AnalyticsData {
  activityTrends: Array<{ date: string; count: number }>;
  userActivity: Array<{ username: string; count: number }>;
  organizationStats: Array<{ organization: string; users: number; activities: number }>;
  exceptionTypes: Array<{ type: string; count: number }>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const Analytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const { organization } = useAuth();

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange, organization]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      if (!organization) return;
      
      // Use efficient endpoints instead of fetching all data
      const [activityCounts, userActivitySummary, exceptions, users] = await Promise.all([
        authAPI.getActivityCounts(),
        authAPI.getUserActivitySummary(),
        authAPI.getExceptions(),
        authAPI.getUsers() // Fetch users from all organizations
      ]);

      // Calculate date range for trends
      const daysBack = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const endTime = Date.now();
      const startTime = endTime - (daysBack * 24 * 60 * 60 * 1000);

      // Get activity trends from the efficient endpoint
      const trendsResponse = await authAPI.getAnalyticsTrends({
        from: startTime,
        to: endTime
      });

      const activityTrends = trendsResponse.activityTrends?.map((trend: ActivityTrend) => ({
        date: new Date(trend.date || 0).toISOString().split('T')[0],
        count: trend.count || 0
      })) || [];

      // Process user activity from summary
      const userActivity = userActivitySummary
        .filter((user: any) => user.lastActivity >= startTime)
        .map((user: any) => ({
          username: user.username,
          count: user.totalActivities
        }))
        .sort((a: { count: number }, b: { count: number }) => b.count - a.count)
        .slice(0, 10);

      // Process organization stats from user summary
      const orgStats: { [key: string]: { users: number; activities: number } } = {};
      userActivitySummary.forEach((user: any) => {
        if (!orgStats[user.organization]) {
          orgStats[user.organization] = { users: 0, activities: 0 };
        }
        orgStats[user.organization].users++;
        orgStats[user.organization].activities += user.totalActivities;
      });

      const organizationStats = Object.entries(orgStats)
        .map(([organization, stats]) => ({ organization, ...stats }))
        .sort((a: { activities: number }, b: { activities: number }) => b.activities - a.activities);

      // Process exception types
      const exceptionCounts: { [key: string]: number } = {};
      exceptions.forEach((exception: any) => {
        const type = exception.description.split(' - ')[0] || 'Unknown';
        exceptionCounts[type] = (exceptionCounts[type] || 0) + 1;
      });

      const exceptionTypes = Object.entries(exceptionCounts)
        .map(([type, count]) => ({ type, count }))
        .sort((a: { count: number }, b: { count: number }) => b.count - a.count);

      setData({
        activityTrends,
        userActivity,
        organizationStats,
        exceptionTypes
      });
    } catch (err: any) {
      setError('Failed to load analytics data');
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Analytics & Trends</Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value as any)}
          >
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Activity Trends */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Activity Trends
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data?.activityTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Exception Types */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Exception Types
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data?.exceptionTypes}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {data?.exceptionTypes.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Users */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Most Active Users
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data?.userActivity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="username" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Organization Stats */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Organization Activity
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data?.organizationStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="organization" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="users" fill="#8884d8" name="Users" />
                <Bar dataKey="activities" fill="#82ca9d" name="Activities" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Summary Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Activities
              </Typography>
              <Typography variant="h4">
                {data?.activityTrends.reduce((sum, item) => sum + item.count, 0) || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Users
              </Typography>
              <Typography variant="h4">
                {data?.userActivity.length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Organizations
              </Typography>
              <Typography variant="h4">
                {data?.organizationStats.length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Exceptions
              </Typography>
              <Typography variant="h4">
                {data?.exceptionTypes.reduce((sum, item) => sum + item.count, 0) || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;
